import { createContext, useContext } from 'react'
import type { NodeIssues } from 'app/lib/workflows/nodeIssues'
import type { MoveDrop, MoveScope } from 'app/types/workflow-graph-edits'

/** Where an append starts: the source node and (for branches) which output handle. */
export type AppendTarget = { nodeId: string; sourceHandle?: string | null; screen: { x: number; y: number } }

/** The edge a "+" insert targets. */
export type InsertEdgeTarget = { id: string; source: string; target: string; sourceHandle?: string | null }

/** Per-node actions exposed to node components (which can't reach the builder's state
 *  directly). Provided by WorkflowBuilderInner so everything stays undoable. */
export type WorkflowActions = {
  deleteNode: (id: string) => void
  duplicateNode: (id: string) => void
  startMove: (id: string) => void
  dropMove: (drop: MoveDrop) => void
  /** Set while a step is being moved: connectors and open ends turn into drop targets. */
  moving: MoveScope | null
  /** Opens the block picker to append a step from an open output, anchored at a screen point. */
  appendFromNode: (target: AppendTarget) => void
  /** Opens the block picker to insert a step on an existing edge, rewiring around it. */
  insertOnEdge: (edge: InsertEdgeTarget, screen: { x: number; y: number }) => void
  /** Removes an edge (disconnects two nodes). */
  breakLink: (edgeId: string) => void
  /** Records an undo point. For an inspector edit that changes a step's exits (removing a reply
   *  option), so undo brings the exit back with its wiring. Ordinary field edits never call it. */
  takeSnapshot: () => void
  /** Precomputed errors/warnings per node id — the single source the card badges read, so they
   *  always match the inspector and the toolbar count. */
  issues: Map<string, NodeIssues>
}

const WorkflowActionsContext = createContext<WorkflowActions | null>(null)

export const WorkflowActionsProvider = WorkflowActionsContext.Provider

export function useWorkflowActions(): WorkflowActions {
  const actions = useContext(WorkflowActionsContext)
  if (!actions) throw new Error(`useWorkflowActions must be used within a WorkflowActionsProvider`)
  return actions
}
